import * as XLSXStyle from "xlsx-js-style";
import {
  headerStyle, totalTextoStyle, totalMoneyStyle,
  celdaMoneda, celdaTexto, celdaNumero, celdaFecha,
  aNumero, sumarImportes, impuestosLocales, nombreVendedor,
} from "./ExcelEstilos";

/**
 * Excel de RESUMEN de la descarga masiva de documentos (cotizaciones, pedidos,
 * embarques, ventas y órdenes de compra). Dos hojas: `Resumen` con un renglón
 * por documento y `Detalle` con un renglón por partida.
 *
 * Es la vista tabular de una selección; el Excel que representa a UN documento
 * (encabezado con logo + tabla de partidas) vive en `ExcelDocumentoIndividual`.
 *
 * Se arma con los MISMOS datos que ya se bajaron para los PDFs (`dataSat`), así
 * que pedir el Excel no cuesta ni una consulta extra al servidor.
 *
 * Devuelve un Blob en vez de escribir el archivo, para poder meterlo al ZIP
 * junto a los PDFs.
 */

const COLUMNAS_ANCHAS = ["Cliente", "Proveedor", "Descripcion", "Proyecto", "Vendedor", "Solicito", "UUID"];
const anchoColumnas = (titulos) =>
  titulos.map((titulo) => (COLUMNAS_ANCHAS.includes(titulo) ? { wch: 30 } : { wch: 16 }));

/**
 * Columnas que un módulo mete al Resumen además de las comunes: la venta pone
 * Tipo y UUID, la orden de compra sus fechas y la requisición de origen. Cada
 * una dice en qué posición entra, para que caigan junto al dato con el que se
 * leen.
 */
const columnasExtraResumen = (modulo) => {
  if (modulo.esVenta) {
    // Las dos van juntas, justo antes de Fecha, como estaban antes de que las
    // columnas extra fueran configurables.
    return [
      { indice: 2, titulo: "Tipo", celda: (values) => celdaTexto(values.factura ? "Factura" : "Remision") },
      { indice: 2, titulo: "UUID", celda: (values, dataSat, mod) => celdaTexto(mod.obtenerUuid ? mod.obtenerUuid(values) : "") },
    ];
  }
  if (modulo.esCompra) {
    return [
      { indice: 3, titulo: "Fecha de entrega", celda: (values, dataSat) => celdaFecha(dataSat.FechaEntrega) },
      { indice: 9, titulo: "Cotizacion", celda: (values, dataSat) => celdaTexto(dataSat.cotizacionOC) },
      { indice: 10, titulo: "Destino", celda: (values, dataSat) => celdaTexto(dataSat.destino) },
    ];
  }
  return [];
};

/**
 * Inserta las columnas extra en su posición. Se agrupan por índice y se meten
 * de atrás hacia adelante: así los índices siguen refiriéndose a la lista base,
 * y varias columnas en el mismo punto quedan en el orden en que se declararon.
 */
const conColumnasExtra = (base, extras, construir) => {
  const fila = [...base];
  const porIndice = new Map();
  for (const extra of extras) {
    if (!porIndice.has(extra.indice)) porIndice.set(extra.indice, []);
    porIndice.get(extra.indice).push(extra);
  }
  for (const indice of [...porIndice.keys()].sort((a, b) => b - a)) {
    fila.splice(indice, 0, ...porIndice.get(indice).map(construir));
  }
  return fila;
};

const hojaResumen = (documentos, modulo) => {
  const esCompra = !!modulo.esCompra;
  const extras = columnasExtraResumen(modulo);

  const titulos = conColumnasExtra([
    "Serie", "Folio", "Fecha", esCompra ? "Proveedor" : "Cliente", "RFC",
    esCompra ? "Solicito" : "Vendedor", "Proyecto",
    esCompra ? "Atencion" : "Orden de compra", "Condiciones de pago",
    "Metodo de pago", "Moneda", "Tipo de cambio", "Subtotal", "Descuento",
    "Impuestos", "Retenciones", "Total", "Estado",
  ], extras, (extra) => extra.titulo);

  const totales = { subtotal: 0, descuento: 0, impuestos: 0, retenciones: 0, total: 0 };

  const filas = documentos.map((values) => {
    const dataSat = values?.respuestaFactura?.dataSat || {};
    const locales = impuestosLocales(dataSat);
    const impuestos = aNumero(dataSat?.Impuestos?.TotalImpuestosTrasladados) + locales.traslados;
    const retenciones = aNumero(dataSat?.Impuestos?.TotalImpuestosRetenidos) + locales.retenciones;

    totales.subtotal += aNumero(dataSat.SubTotal);
    totales.descuento += aNumero(dataSat.Descuento);
    totales.impuestos += impuestos;
    totales.retenciones += retenciones;
    totales.total += aNumero(dataSat.Total);

    return conColumnasExtra([
      celdaTexto(dataSat.Serie),
      // Numérico para que Excel ordene por folio de verdad, no como texto.
      celdaNumero(dataSat.Folio),
      celdaFecha(dataSat.Fecha),
      celdaTexto(dataSat.Receptor?.Nombre),
      celdaTexto(dataSat.Receptor?.Rfc),
      celdaTexto(esCompra ? dataSat.nombreCreador : nombreVendedor(values.vendedor)),
      celdaTexto(esCompra ? dataSat.ProyectoNombre : dataSat.proyecto),
      celdaTexto(esCompra ? dataSat.atencion : dataSat.pedido),
      celdaTexto(dataSat.CondicionesDePago),
      celdaTexto(esCompra ? dataSat.metodoPago : dataSat.MetodoPago),
      celdaTexto(dataSat.Moneda),
      celdaNumero(dataSat.TipoCambio),
      celdaMoneda(dataSat.SubTotal),
      celdaMoneda(dataSat.Descuento),
      celdaMoneda(impuestos),
      celdaMoneda(retenciones),
      celdaMoneda(dataSat.Total),
      celdaTexto(modulo.etiquetaEstado(values)),
    ], extras, (extra) => extra.celda(values, dataSat, modulo));
  });

  const indiceSubtotal = titulos.indexOf("Subtotal");
  const filaTotales = titulos.map((titulo, indice) => {
    if (indice === 0) return celdaTexto("TOTAL", totalTextoStyle);
    if (indice === indiceSubtotal) return celdaMoneda(totales.subtotal, totalMoneyStyle);
    if (indice === indiceSubtotal + 1) return celdaMoneda(totales.descuento, totalMoneyStyle);
    if (indice === indiceSubtotal + 2) return celdaMoneda(totales.impuestos, totalMoneyStyle);
    if (indice === indiceSubtotal + 3) return celdaMoneda(totales.retenciones, totalMoneyStyle);
    if (indice === indiceSubtotal + 4) return celdaMoneda(totales.total, totalMoneyStyle);
    return celdaTexto("", totalTextoStyle);
  });

  const hoja = XLSXStyle.utils.aoa_to_sheet([
    titulos.map((titulo) => celdaTexto(titulo, headerStyle)),
    ...filas,
    filaTotales,
  ]);
  hoja["!cols"] = anchoColumnas(titulos);
  return hoja;
};

const hojaDetalle = (documentos, modulo) => {
  const esCompra = !!modulo.esCompra;
  const titulos = [
    "Serie", "Folio", "Fecha", esCompra ? "Proveedor" : "Cliente",
    esCompra ? "Clave Int." : "Clave", esCompra ? "Clave Prov." : "Clave SAT",
    "Descripcion", "Unidad", "Cantidad", "Precio unitario", "Importe",
    "Descuento", "Impuestos", "Retenciones", "Total",
    ...(esCompra ? ["Fecha entrega", "Observaciones"] : []),
  ];

  const filas = [];
  for (const values of documentos) {
    const dataSat = values?.respuestaFactura?.dataSat || {};
    for (const concepto of dataSat.Conceptos || []) {
      const importe = aNumero(concepto.Importe);
      const descuento = aNumero(concepto.Descuento);
      const impuestos = sumarImportes(concepto?.Impuestos?.Traslados);
      const retenciones = sumarImportes(concepto?.Impuestos?.Retenciones);
      filas.push([
        celdaTexto(dataSat.Serie),
        celdaNumero(dataSat.Folio),
        celdaFecha(dataSat.Fecha),
        celdaTexto(dataSat.Receptor?.Nombre),
        celdaTexto(esCompra ? concepto.ClaveInt : concepto.NoIdentificacion),
        celdaTexto(esCompra ? concepto.ClaveProv : concepto.ClaveProdServ),
        celdaTexto(concepto.Descripcion),
        celdaTexto(esCompra ? (concepto.descUnidad || concepto.Unidad) : concepto.Unidad),
        celdaNumero(concepto.Cantidad),
        celdaMoneda(concepto.ValorUnitario),
        celdaMoneda(importe),
        celdaMoneda(descuento),
        celdaMoneda(impuestos),
        celdaMoneda(retenciones),
        celdaMoneda(importe - descuento + impuestos - retenciones),
        ...(esCompra
          ? [celdaFecha(concepto.FechaEntrega), celdaTexto(concepto.Observaciones)]
          : []),
      ]);
    }
  }

  const hoja = XLSXStyle.utils.aoa_to_sheet([
    titulos.map((titulo) => celdaTexto(titulo, headerStyle)),
    ...filas,
  ]);
  hoja["!cols"] = anchoColumnas(titulos);
  return hoja;
};

/**
 * @param {Object[]} documentos - los `values` (el mismo objeto que consume el PDF)
 * @param {Object} modulo - configuración del módulo (`esVenta`, `esCompra`,
 *                          `etiquetaEstado`, `obtenerUuid`)
 * @returns {Blob} el .xlsx listo para descargar o meter al ZIP
 */
const construirExcelDocumentos = (documentos, modulo) => {
  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hojaResumen(documentos, modulo), "Resumen");
  XLSXStyle.utils.book_append_sheet(libro, hojaDetalle(documentos, modulo), "Detalle");

  const buffer = XLSXStyle.write(libro, { bookType: "xlsx", type: "array" });
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export default construirExcelDocumentos;
